import AddressField from "kinnect-front/Address/AddressField";
import CustomerField from "kinnect-front/KCustomers/CustomerField";
import SalesmanField from "kinnect-front/KSalesmen/SalesmanField";
import {
	Address,
	Customer,
	Salesman,
	YardScheduleType
} from "kinnect-models";
import { FetchesData } from "kinnect-mixins";
import {
	KAddressFormBox,
	KAutocomplete,
	KCombobox,
	KDatePicker,
	KForm,
	KListTile,
	KTextField,
	KTextarea,
	KTimePicker
} from "kinnect-general";
import { VCol, VRow } from "vuetify/lib";
import { formatDate } from "kinnect-formatters";
import { isSunday, parseISO } from "date-fns";
import { sortByKey } from "kinnect-helpers";

export default KForm.extend( {
	name: "KScheduleForm",

	props: {
		redirect: {
			...KForm.options.props.redirect,
			default: () => [ "back", { name: "yardSchedules" } ]
		},

		schedule: Object
	},

	mixins: [ FetchesData ],

	data: () => ( {
		customers: [],
		salesmen: [],
		scheduleTypes: [],
		addressFormFields: {
			address: null,
			city: null,
			lat: null,
			lng: null,
			state: null,
			zip: null
		},
		formFields: {
			deliveryDate: null,
			deliveryTime: null,
			description: null,
			estLoadTime: null,
			idAddress: null,
			idCustomer: null,
			idSalesman: null,
			idsScheduleTypes: [],
			purchaseOrders: [],
			salesOrders: []
		},
		salesOrderNumbers: [],
		purchaseOrderNumbers: [],
		today: ( new Date() ).setHours( 0, 0, 0, 0 )
	} ),

	computed: {
		editing() {
			return Boolean( this.schedule );
		},

		todayFormatted() {
			return formatDate( this.today, "yyyy-MM-dd" );
		},

		indexSelectedCustomer() {
			return this.customers.findIndex( customer => this.formFields.idCustomer === customer.id );
		},

		selectedCustomer() {
			if ( this.indexSelectedCustomer !== -1 ) {
				return this.customers[this.indexSelectedCustomer];
			}
		},

		customerAddresses() {
			if ( this.selectedCustomer && this.selectedCustomer.addresses ) {
				return this.selectedCustomer.addresses;
			}

			return [];
		}
	},

	watch: {
		schedule: {
			handler( schedule ) {
				if ( schedule ) {
					Object.keys( this.formFields ).forEach( key => {
						if ( schedule.hasOwnProperty( key )
							&& key !== "address"
							&& key !== "purchaseOrders"
							&& key !== "salesOrders"
						) {
							this.formFields[key] = schedule[key];
						}
					} );

					this.formFields.idsScheduleTypes = schedule.scheduleTypes.map( item => item.id );

					this.addressFormFields.address = this.schedule.address.address;
					this.addressFormFields.city = this.schedule.address.city;
					this.addressFormFields.state = this.schedule.address.state;
					this.addressFormFields.zip = this.schedule.address.zip;

					this.purchaseOrderNumbers = schedule.purchaseOrders.map( item => item.purchaseOrderNumber );
					this.salesOrderNumbers = schedule.salesOrders.map( item => item.salesOrderNumber );
				}
			},
			immediate: true
		},

		"formFields.idCustomer"( newVal, oldVal ) {

			// If switching between customers, reset the address field
			if ( oldVal ) {
				this.formFields.idAddress = null;
			}
		},

		purchaseOrderNumbers: {
			handler( val ) {
				this.formFields.purchaseOrders = val.map( purchaseOrderNumber => ( { purchaseOrderNumber } ) );
			},
			immediate: true
		},

		salesOrderNumbers: {
			handler( val ) {
				this.formFields.salesOrders = val.map( salesOrderNumber => ( { salesOrderNumber } ) );
			},
			immediate: true
		}
	},

	methods: {
		submit() {
			if ( this.formFields.idAddress ) {
				return this.submitSchedule();
			}

			return this.createAddress().then( this.submitSchedule );
		},

		createAddress() {
			const newAddress = new Address( this.addressFormFields );

			return newAddress
			.save( false )
			.then( address => this.formFields.idAddress = address.id );
		},

		submitSchedule() {
			const method = this.editing ? "put" : "post";
			const url = this.editing
				? `yard/schedules/${this.schedule.id}`
				: "yard/schedules";

			return this.$http[method]( url, this.formFields );
		},

		isDateAllowed( date ) {
			return !isSunday( parseISO( date ) );
		},

		onCreateCustomer( customer ) {
			this.customers.push( customer );
			this.formFields.idCustomer = customer.id;
		},

		onCreateAddress( address ) {
			if ( this.indexSelectedCustomer === -1 ) {
				return;
			}

			this.customers[this.indexSelectedCustomer].addresses.push( address );
			this.formFields.idAddress = address.id;
		},

		onCreateSalesman( salesman ) {
			this.salesmen.push( salesman );
			this.formFields.idSalesman = salesman.id;
		},

		async fetchScheduleTypes() {
			const scheduleTypes = await YardScheduleType.get();

			this.scheduleTypes = sortByKey( scheduleTypes, "name" );
		},

		async fetchCustomers() {
			this.customers = await Customer.get();
		},

		async fetchSalesmen() {
			this.salesmen = await Salesman.get();
		},

		async fetch() {
			await this.$http.all( [
				this.fetchScheduleTypes(),
				this.fetchCustomers(),
				this.fetchSalesmen()
			] );
		}
	},

	render() {
		return <KForm
			attrs={{
				...this.$props,
				manualSubmitCall: this.submit
			}}
			on={this.$listeners}
		>
			<VRow>
				<VCol class="py-0" cols={12} sm={6}>
					<KDatePicker
						datePickerProps={{
							allowedDates: this.isDateAllowed,
							min: this.todayFormatted
						}}
						textFieldProps={{ label: "Delivery Date" }}
						vModel={this.formFields.deliveryDate}
					></KDatePicker>
				</VCol>

				<VCol class="py-0" cols={12} sm={6}>
					<KTimePicker
						textFieldProps={{ label: "Delivery Time" }}
						vModel={this.formFields.deliveryTime}
					></KTimePicker>
				</VCol>
			</VRow>

			<KAutocomplete
				chips
				class="ml-blank-icon"
				deletableChips
				delimiters={[ ",", " " ]}
				itemText="name"
				itemValue="id"
				items={this.scheduleTypes}
				label="Type(s)*"
				loading={this.$wait.is( "get schedule types" )}
				multiple
				required
				vModel={this.formFields.idsScheduleTypes}
				validationName="schedule type(s)"
				validationRules="required"
			></KAutocomplete>

			<CustomerField
				deletableChips
				items={this.customers}
				label="Customer"
				loading={this.$wait.is( "get customers" )}
				onCreate={this.onCreateCustomer}
				showAddButton={this.$can( "add_customers" )}
				vModel={this.formFields.idCustomer}
			></CustomerField>

			<transition name="slide-y-transition" mode="out-in">
				{this.selectedCustomer
					? <AddressField
						class="mb-4"
						customer={this.selectedCustomer}
						items={this.customerAddresses}
						label="Customer Address*"
						loading={this.$wait.is( "get customers" )}
						onCreate={this.onCreateAddress}
						required
						showAddButton={this.$can( "add_addresses" )}
						vModel={this.formFields.idAddress}
						validationName="customer address"
						validationRules="required"
					></AddressField>
					: <KAddressFormBox
						address={this.addressFormFields.address}
						city={this.addressFormFields.city}
						class="mb-4 ml-blank-icon"
						lat={this.addressFormFields.lat}
						lng={this.addressFormFields.lng}
						on={{
							"update:address": val => this.addressFormFields.address = val,
							"update:city": val => this.addressFormFields.city = val,
							"update:lat": val => this.addressFormFields.lat = val,
							"update:lng": val => this.addressFormFields.lng = val,
							"update:state": val => this.addressFormFields.state = val,
							"update:zip": val => this.addressFormFields.zip = val
						}}
						onPlacechanged={() => this.formFields.idAddress = null}
						required
						state={this.addressFormFields.state}
						zip={this.addressFormFields.zip}
					></KAddressFormBox>
				}
			</transition>

			{this.$can( "add_yard_schedules" ) && <KTextField
				label="Estimated Loading Time"
				min="0"
				onInput={val => this.formFields.estLoadTime = val ? parseFloat( val ) : null}
				prependIcon="timer"
				step="5"
				suffix="minutes"
				type="number"
				validationName="estimated loading time"
				validationRules="min_value:0"
				value={this.formFields.estLoadTime}
			></KTextField>}

			<SalesmanField
				items={this.salesmen}
				label="Salesman*"
				loading={this.$wait.is( "get salesmen" )}
				onCreate={this.onCreateSalesman}
				required
				showAddButton={this.$can( "add_salesmen" )}
				vModel={this.formFields.idSalesman}
				validationName="salesman"
				validationRules="required"
			></SalesmanField>

			<KCombobox
				chips
				deletableChips
				delimiters={[ ",", " " ]}
				label="Sales Order Numbers"
				multiple
				prependIcon="format_list_bulleted"
				vModel={this.salesOrderNumbers}
				validationName="sales order numbers"
			>
				<KListTile slot="no-data" text="Enter a new sales order number"></KListTile>
			</KCombobox>

			<KCombobox
				chips
				deletableChips
				delimiters={[ ",", " " ]}
				label="Purchase Order Numbers"
				multiple
				prependIcon="format_list_bulleted"
				vModel={this.purchaseOrderNumbers}
				validationName="purchase order numbers"
			>
				<KListTile slot="no-data" text="Enter a new purchase order number"></KListTile>
			</KCombobox>

			<KTextarea
				label="Description"
				name="description"
				prependIcon="comment"
				vModel={this.formFields.description}
			></KTextarea>
		</KForm>;
	}
} );
